import { Controller } from "@hotwired/stimulus"

// Chat window. Messages are matched by server id, so history loading, retries and page restores never
// show the same message twice; a failed send puts the text back in the input and can be retried safely
// (the same request id is reused while the text is unchanged).
export default class ChatWidgetController extends Controller {
  static targets = ["messages", "input", "button", "container"]
  static values = {
    locale: String, thinking: String, failed: String,
    errors: Object, timeout: { type: Number, default: 45000 }, maxLength: { type: Number, default: 2000 }
  }

  connect() {
    this.composing = false
    this.lastCompositionEnd = 0
    this.pending = null
    this.sending = false
    this.historyReady = this.loadHistory()
  }

  async loadHistory() {
    // A slow history request must not keep the visitor from sending: give up after a few seconds.
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 8000)
    try {
      const response = await fetch("/api/chat/history", { headers: { Accept: "application/json" }, signal: controller.signal })
      const data = await response.json()
      if (data.messages) this.renderMessages(data.messages)
    } catch (e) {
      console.error("Failed to load history:", e)
    } finally {
      clearTimeout(timer)
    }
  }

  async sendMessage() {
    if (this.sending) return

    const content = this.inputTarget.value.trim()
    if (!content) return
    if (content.length > this.maxLengthValue) return this.showError("too_long")

    this.sending = true
    // The history must be in the window first, or the same message could arrive twice.
    await this.historyReady

    const retrying = this.pending && this.pending.text === content
    if (!retrying) this.pending = { id: this.newRequestId(), text: content }
    const requestId = this.pending.id

    this.inputTarget.value = ""
    const bubble = this.addMessage("user", content, { requestId })
    this.setLoading(true)

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), this.timeoutValue)
    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        signal: controller.signal,
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({ message: content, request_id: requestId, locale: this.localeValue })
      })
      const data = await response.json().catch(() => ({}))

      if (response.ok && data.reply) {
        if (data.user_message_id) bubble.dataset.messageId = data.user_message_id
        if (!this.hasMessage(data.assistant_message_id)) {
          this.addMessage("assistant", data.reply, { id: data.assistant_message_id, action: data.action })
        }
        this.pending = null
      } else {
        this.failSend(bubble, content, data.error || "unavailable")
      }
    } catch (e) {
      this.failSend(bubble, content, e.name === "AbortError" ? "timeout" : "unavailable")
    } finally {
      clearTimeout(timer)
      this.setLoading(false)
      this.sending = false
      this.scrollToBottom()
    }
  }

  // The message was not answered: take it out of the window, give the text back, say why.
  failSend(bubble, content, type) {
    bubble.remove()
    this.inputTarget.value = content
    this.showError(type)
  }

  showError(type) {
    const text = this.errorsValue[type] || this.failedValue
    this.addMessage("assistant", text)
  }

  addMessage(role, content, { id, requestId, action } = {}) {
    const messageDiv = document.createElement("div")
    messageDiv.className = role === "user" ? "flex justify-end mb-3" : "flex justify-start mb-3"
    if (id) messageDiv.dataset.messageId = id
    if (requestId) messageDiv.dataset.requestId = requestId

    const bubbleClass = role === "user"
      ? "bg-primary text-white rounded-lg px-4 py-2 max-w-[80%]"
      : "bg-gray-100 text-gray-800 rounded-lg px-4 py-2 max-w-[80%]"
    const bubble = document.createElement("div")
    bubble.className = `${bubbleClass} text-sm whitespace-pre-wrap`
    bubble.textContent = content

    // An offered next step is a plain text link; its address comes from the server, never from the model's text.
    if (action && action.url && action.label) {
      const line = document.createElement("div")
      line.className = "mt-2"
      const link = document.createElement("a")
      link.href = action.url
      link.className = "underline font-medium"
      link.textContent = action.label
      line.appendChild(link)
      bubble.appendChild(line)
    }

    messageDiv.appendChild(bubble)
    this.messagesTarget.appendChild(messageDiv)
    this.scrollToBottom()
    return messageDiv
  }

  renderMessages(messages) {
    messages.forEach(msg => {
      if (!this.hasMessage(msg.id)) this.addMessage(msg.role, msg.content, { id: msg.id, action: msg.action })
    })
  }

  hasMessage(id) {
    return !!id && !!this.messagesTarget.querySelector(`[data-message-id="${id}"]`)
  }

  scrollToBottom() {
    setTimeout(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }, 50)
  }

  setLoading(loading) {
    this.buttonTarget.disabled = loading
    this.inputTarget.disabled = loading
    if (loading) {
      this.addMessage("assistant", this.thinkingValue)
      this.loadingMessage = this.messagesTarget.lastElementChild
    } else {
      if (this.loadingMessage) {
        this.loadingMessage.remove()
        this.loadingMessage = null
      }
      this.inputTarget.focus()
    }
  }

  toggle() {
    this.containerTarget.classList.toggle("hidden")
  }

  // Enter sends, Shift+Enter does not, and Enter that merely confirms an IME candidate (e.g. pinyin) is ignored.
  handleKeydown(event) {
    if (event.key !== "Enter" || event.shiftKey) return
    if (event.isComposing || this.composing || event.keyCode === 229) return
    if (Date.now() - this.lastCompositionEnd < 30) return

    event.preventDefault()
    this.sendMessage()
  }

  compositionStart() {
    this.composing = true
  }

  compositionEnd() {
    this.composing = false
    this.lastCompositionEnd = Date.now()
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  newRequestId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  }
}
